import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { DepotNameComponent } from "@app/directives/_misc/depotName/depotName.component";
import { IAggregateFilterEvent } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { DateService } from "@app/services/date.service";
import { GroupService } from "@app/services/groups/group.service";
import { DateRange } from "@enums/DateRange.enum";
import { SortType } from "@enums/SortType";
import { IGeofence } from "@interfaces/IGeofences";
import { IVehicle } from "@interfaces/IVehicle";
import { GeofenceService, IGeofenceVisit } from "@services/geofence.service";
import { TranslationService } from "@services/translation.service";
import { VehicleService } from "@services/vehicle.service";
import { VeriDate } from "@services/veriDate.service";
import { IGroupWithIds } from "../../resourceManagement/groups/_models/IGroupWithIds";
import "./depot-compliance.component.scss";


interface IVehicleDepotVisitBreakdown {
    geofence: IGeofence;
    depot?: IGroupWithIds;
    duration?: number;
    durationString?: string;
    percentage?: number;
}

interface IVehicleDepotRecord extends IVehicle {
    allGeofenceVisits?: IGeofenceVisit[],
    allDepotVisits?: IGeofenceVisit[];
    homeDepotGroup?: IGroupWithIds,
    mostVisitedDepot?: IGroupWithIds;
    depotVisitBreakdown?: IVehicleDepotVisitBreakdown[];
    homeVisits?: IGeofenceVisit[],
}


@Component({
    selector: "depot-compliance",
    template: require('./depot-compliance.component.html')
})
export class DepotComplianceComponent implements OnInit {

    loading = false;

    defaultDateRange = DateRange.ThisMonth;
    startDate: Date;
    endDate: Date;


    tableColumns: IVTableColumn<IVehicleDepotRecord>[];
    tableRows: IVTableRow[];

    selectedVehicleTableColumns: IVTableColumn<IVehicleDepotVisitBreakdown>[];
    selectedVehicleTableRows: IVTableRow[];

    vehicles: IVehicleDepotRecord[];
    selectedVehicle: IVehicleDepotRecord;

    geofences: IGeofence[];
    geofenceVisits: IGeofenceVisit[];

    depots: IGroupWithIds[];

    timeWindow: number;

    @ViewChild('mostVisitedColumn') mostVisitedColumn: TemplateRef<any>;
    constructor(
        private readonly dateService: DateService,
        private readonly groupService: GroupService,
        private readonly geofenceService: GeofenceService,
        private readonly vehicleService: VehicleService,
        private readonly veriDate: VeriDate,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService,
    ) {
    }


    ngOnInit(): void {
        this.startDate = this.veriDate.dateRangeFromEnum(this.defaultDateRange).start;
        this.endDate = this.veriDate.dateRangeFromEnum(this.defaultDateRange).end;
        this.createTable();
        this.loadData();
    }

    selectedVehicleChanged(row: IVTableRow) {
        this.selectedVehicle = row.data;
        this.createSelectedVehicleTable();
        this.createSelectedVehicleTableRows();
    }

    clearSelectedVehicle() {
        this.selectedVehicle = null;
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<IVehicleDepotRecord>) {

        if (filterEvent.trigger == "date") {
            this.startDate = filterEvent.date[0];
            this.endDate = filterEvent.date[1];
            this.setTimeWindow();
            this.loadData();
            return;
        }
    }


    getExportTitle() {
        return `${this.translationService.translate('DepotCompliance.DepotCompliance')} - ${this.veriDate.createMoment(this.startDate).format('DD-MM-YYYY')} to ${this.veriDate.createMoment(this.endDate).format('DD-MM-YYYY')}`;
    }

    //--

    private loadData() {
        this.loading = true;
        const promises: any[] = [];
        promises.push(this.vehicleService.getVehicles({ includeLatestPlots: true }));
        promises.push(this.groupService.getGroupsWithIds());
        promises.push(this.geofenceService.getGeofences());
        promises.push(this.geofenceService.getGeofenceVisits(this.veriDate.createMoment(this.startDate), this.veriDate.createMoment(this.endDate), true));
        Promise.all(promises).then(([vehicles, groups, geofences, visits]) => {
            this.vehicles = vehicles;
            this.geofences = geofences;
            this.depots = this.groupService.findDepotsWithHomeLocations(groups);
            this.mapDepotToVehicles();
            this.mapVisitsToVehicles(visits);
            this.getDepotVisitsBreakdown()
        }).finally(() => {
            this.tableRows = this.vTableService.createTableRows(this.vehicles, this.tableColumns);
            this.loading = false;
        })
    }

    private mapDepotToVehicles() {
        this.vehicles.forEach(vehicle => {
            vehicle.homeDepotGroup = this.depots.find(x => x.VehicleIds.includes(vehicle.Id));
        })
    }

    private setTimeWindow(): void {
        const startDate = this.startDate;
        const endDate = this.dateService.date1IsAfterDate2(this.endDate, new Date()) ? new Date() : this.endDate;
        this.timeWindow = endDate.getTime() - startDate.getTime();
    }

    private mapVisitsToVehicles(visits: IGeofenceVisit[]) {
        visits.forEach(visit => {
            this.getVisitDuration(visit);
            visit.geofence = this.geofenceService.findGeofence(this.geofences, visit.GeofenceId);
        });
        this.vehicles.forEach(vehicle => {
            vehicle.allGeofenceVisits = visits.filter(visit => visit.VehicleId === vehicle.Id);
            vehicle.allGeofenceVisits.sort((a, b) => {
                if (a.duration > b.duration) {
                    return -1;
                }
            });
            vehicle.allDepotVisits = vehicle.allGeofenceVisits.filter(visit => this.isVisitLocationADepot(visit) && this.visitIsOver9Hours(visit));
            vehicle.homeVisits = this.geofenceService.findGeofenceVisitsToDepotsOrHomeLocations(vehicle.allGeofenceVisits, this.depots);
        });
    }

    private getDepotVisitsBreakdown() {
        this.vehicles.forEach(vehicle => {
            vehicle.depotVisitBreakdown = [];
            vehicle.allDepotVisits.forEach(visit => {
                const location = vehicle.depotVisitBreakdown.find(dv => {
                    return dv.geofence.Id === visit.GeofenceId;
                });
                if (location) {
                    location.duration += visit.duration;
                    return;
                }

                const depot = this.depots.find(d => d.GeofenceId === visit.GeofenceId);
                vehicle.depotVisitBreakdown.push({
                    geofence: this.geofenceService.findGeofence(this.geofences, visit.GeofenceId),
                    depot,
                    duration: visit.duration,
                    percentage: 0
                });

            });

            const totalDuration = vehicle.depotVisitBreakdown.reduce((prev, curr) => {
                return prev + curr.duration;
            }, 0);
            vehicle.depotVisitBreakdown.forEach(depotBreakdown => {
                depotBreakdown.percentage = (depotBreakdown.duration / totalDuration * 100);
                depotBreakdown.durationString = this.veriDate.formatDurationMs(depotBreakdown.duration);
            })
            vehicle.depotVisitBreakdown.sort((a, b) => {
                if (a.duration > b.duration) {
                    return -1;
                };
            });

            this.findMostVisitedDepotsForVehicle(vehicle);
        });
    }

    private getVisitDuration(visit: IGeofenceVisit) {
        const startTime = new Date(visit.StartUtc);
        const endTime = visit.EndUtc ? new Date(visit.EndUtc) : new Date();
        visit.duration = endTime.getTime() - startTime.getTime();
        visit.durationFormatted = this.veriDate.formatDurationMs(visit.duration);
    }

    private getTotalTimeAtDepots(vehicle: IVehicleDepotRecord): number {
        let totalTime = 0;
        totalTime = vehicle.allDepotVisits.reduce((prev, curr) => {
            return prev + curr.duration;
        }, 0);
        if (totalTime > this.timeWindow) {
            // If a vehicle has been parked since before the start date on the
            // date filter, it will return a longer time than the expected window
            totalTime = this.timeWindow;
        }
        return totalTime;
    }

    private findMostVisitedDepotsForVehicle(vehicle: IVehicleDepotRecord) {
        if (!vehicle.depotVisitBreakdown?.length) {
            return;
        }
        vehicle.mostVisitedDepot = this.depots.find(depot => {
            return depot.GeofenceId === vehicle.depotVisitBreakdown[0].geofence.Id;
        });
        return vehicle.mostVisitedDepot;

    }

    private isVisitLocationADepot(visit: IGeofenceVisit) {
        const depot = this.depots.find(depot => depot.GeofenceId === visit.GeofenceId);
        if (depot) {
            return true;
        }
        else {
            return false;
        }
    }

    private visitIsOver9Hours(visit: IGeofenceVisit) {
        return visit.duration > 9 * 60 * 60 * 1000;
    }

    private getRowWarningStatus(vehicle: IVehicleDepotRecord): VTableRowStatus {
        if (!vehicle.mostVisitedDepot || !vehicle.homeDepotGroup) {
            return VTableRowStatus.Info;
        }
        if (vehicle.homeDepotGroup.GeofenceId === vehicle.mostVisitedDepot?.GeofenceId) {
            return VTableRowStatus.Info;
        }
        else {
            return VTableRowStatus.Error;
        }
    }

    private createTable() {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader(),
                createCellFunction: (vehicle) => this.vTableService.createStatusCell(this.getRowWarningStatus(vehicle)),
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (vehicle) => this.vTableService.createVehicleRegCell(vehicle),
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('DepotCompliance.HomeDepot'), sortable: true }),
                createCellFunction: (vehicle) => {
                    const depot = vehicle.homeDepotGroup;
                    if (!depot) {
                        return this.vTableService.createTableCell(this.translationService.t("DepotCompliance.NoDepotOrHomeSpecified"), {classes: 'text-muted'})
                    }
                    return this.vTableService.createTableCell(depot?.Name, { exportData: depot?.Name, sortData: depot?.Name, component: DepotNameComponent, componentInputs: { name: depot?.Name } });
                }
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('DepotCompliance.MostVisitedDepot'), sortable: true }),
                createCellFunction: (vehicle) => {
                    const mostVisited = this.findMostVisitedDepotsForVehicle(vehicle);
                    const mostVisitedPercentage = vehicle.depotVisitBreakdown.length ? vehicle.depotVisitBreakdown[0].percentage : null;
                    const templateData = {
                        name: mostVisited?.Name || null,
                        percentage: mostVisitedPercentage
                    }
                    return this.vTableService.createTableTemplateCell(templateData, this.mostVisitedColumn, { exportData: mostVisited?.Name || "--", sortData: mostVisited?.Name });
                }
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('DepotCompliance.TotalTimeSpent'), sortable: true, sortType: SortType.Number }),
                createCellFunction: (vehicle) => {
                    const totalTimeSpent = this.getTotalTimeAtDepots(vehicle);
                    return this.vTableService.createTableCell(this.dateService.formatTimeFromMilliseconds(totalTimeSpent), { sortData: totalTimeSpent, exportData: this.dateService.formatTimeFromMilliseconds(totalTimeSpent) });
                }
            },
        ];
    }

    private createSelectedVehicleTable() {

        this.selectedVehicleTableColumns = [
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('DepotCompliance.Depot'), sortable: true, sortType: SortType.String }),
                createCellFunction: (visitBreakdown) => this.vTableService.createTableCell(visitBreakdown.depot?.Name, { component: DepotNameComponent, componentInputs: { name: visitBreakdown.depot?.Name } })
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('DepotCompliance.TotalDuration'), sortable: true, sortType: SortType.Number }),
                createCellFunction: (visitBreakdown) => this.vTableService.createTableCell(visitBreakdown.durationString, { sortData: visitBreakdown.duration })
            },
            {
                header: this.vTableService.createTableHeader({ title: this.translationService.t('DepotCompliance.Percentage'), sortable: true, sortType: SortType.Number }),
                createCellFunction: (visitBreakdown) => this.vTableService.createTableCell(visitBreakdown.percentage.toFixed(1) + '%', { sortData: visitBreakdown.percentage })
            }
        ]

    }

    private createSelectedVehicleTableRows() {
        this.selectedVehicleTableRows = this.vTableService.createTableRows(this.selectedVehicle.depotVisitBreakdown, this.selectedVehicleTableColumns);
    }
}
